import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { getUsers, deleteUser } from "services/users";
import logger from "utils/logger";

export default function CardUsersTable({ color, onEdit, onUserDeleted }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAdmin, setFilterAdmin] = useState("all");

  useEffect(() => {
    loadUsers();
  }, [searchTerm, filterAdmin]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = {};

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (filterAdmin !== "all") {
        params.admin = filterAdmin === "admin" ? 1 : 0;
      }

      const response = await getUsers(params);
      setUsers(response.data.data.data || []);
      setError(null);
    } catch (err) {
      logger.error("Error loading users:", err);
      setError("Error al cargar los usuarios");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`¿Estás seguro de eliminar al usuario "${userName}"?`)) {
      return;
    }

    try {
      await deleteUser(userId);
      loadUsers();
      if (onUserDeleted) {
        onUserDeleted();
      }
    } catch (err) {
      logger.error("Error deleting user:", err);
      const errorMessage = err.response?.data?.message || "Error al eliminar el usuario";
      alert(errorMessage);
    }
  };

  return (
    <>
      <div
        className={
          "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded " +
          (color === "light" ? "bg-white" : "bg-lightBlue-900 text-white")
        }
      >
        <div className="rounded-t mb-0 px-4 py-3 border-0">
          <div className="flex flex-wrap items-center">
            <div className="relative w-full px-4 max-w-full flex-grow flex-1">
              <h3
                className={
                  "font-semibold text-lg " +
                  (color === "light" ? "text-blueGray-700" : "text-white")
                }
              >
                Gestión de Usuarios
              </h3>
            </div>
            <div className="relative w-full px-4 max-w-full flex-grow flex-1 text-right">
              <button
                className="bg-lightBlue-500 text-white active:bg-lightBlue-600 text-xs font-bold uppercase px-3 py-1 rounded outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                type="button"
                onClick={() => onEdit(null)}
              >
                Nuevo Usuario
              </button>
            </div>
          </div>

          {/* Search and filters */}
          <div className="flex flex-wrap items-center mt-4 gap-2">
            <div className="relative flex-grow">
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                className="border-0 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="relative">
              <select
                className="border-0 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring ease-linear transition-all duration-150"
                value={filterAdmin}
                onChange={(e) => setFilterAdmin(e.target.value)}
              >
                <option value="all">Todos los usuarios</option>
                <option value="admin">Solo administradores</option>
                <option value="regular">Solo usuarios regulares</option>
              </select>
            </div>
          </div>
        </div>

        <div className="block w-full overflow-x-auto">
          {loading ? (
            <div className="text-center py-8">
              <i className="fas fa-spinner fa-spin text-blueGray-400 text-2xl"></i>
              <p className="text-blueGray-400 mt-2">Cargando usuarios...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500">{error}</p>
              <button
                onClick={loadUsers}
                className="mt-2 text-lightBlue-500 hover:text-lightBlue-600"
              >
                Reintentar
              </button>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-blueGray-400">No se encontraron usuarios</p>
            </div>
          ) : (
            <table className="items-center w-full bg-transparent border-collapse">
              <thead>
                <tr>
                  <th
                    className={
                      "px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                      (color === "light"
                        ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                        : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                    }
                  >
                    Usuario
                  </th>
                  <th
                    className={
                      "px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                      (color === "light"
                        ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                        : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                    }
                  >
                    Email
                  </th>
                  <th
                    className={
                      "px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                      (color === "light"
                        ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                        : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                    }
                  >
                    Rol
                  </th>
                  <th
                    className={
                      "px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                      (color === "light"
                        ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                        : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                    }
                  >
                    Plan
                  </th>
                  <th
                    className={
                      "px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                      (color === "light"
                        ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                        : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                    }
                  >
                    Registro
                  </th>
                  <th
                    className={
                      "px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left " +
                      (color === "light"
                        ? "bg-blueGray-50 text-blueGray-500 border-blueGray-100"
                        : "bg-lightBlue-800 text-lightBlue-300 border-lightBlue-700")
                    }
                  >
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                      <div className="flex items-center">
                        <span className="h-8 w-8 rounded-full bg-blueGray-200 flex items-center justify-center text-blueGray-500 font-semibold mr-2">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                        <span
                          className={
                            "font-bold " +
                            (color === "light" ? "text-blueGray-600" : "text-white")
                          }
                        >
                          {user.name}
                        </span>
                      </div>
                    </td>
                    <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                      {user.email}
                    </td>
                    <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                      {user.admin ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          <i className="fas fa-crown mr-1"></i>
                          Administrador
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blueGray-100 text-blueGray-800">
                          <i className="fas fa-user mr-1"></i>
                          Usuario
                        </span>
                      )}
                    </td>
                    <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                      {user.plan ? (
                        <span className="text-blueGray-600">{user.plan.name}</span>
                      ) : (
                        <span className="text-blueGray-400 italic">Sin plan</span>
                      )}
                    </td>
                    <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                      <button
                        className="text-lightBlue-500 hover:text-lightBlue-600 mr-3"
                        onClick={() => onEdit(user)}
                        title="Editar usuario"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="text-red-500 hover:text-red-600"
                        onClick={() => handleDelete(user.id, user.name)}
                        title="Eliminar usuario"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

CardUsersTable.defaultProps = {
  color: "light",
};

CardUsersTable.propTypes = {
  color: PropTypes.oneOf(["light", "dark"]),
  onEdit: PropTypes.func.isRequired,
  onUserDeleted: PropTypes.func,
};
